import React, { useState, useEffect } from 'react';
import { api } from '../api';
import { useTranslation } from 'react-i18next';

// Error types
const ERR_DENIED   = 'denied';    // any auth/access failure — show "Authorisation Required"
const ERR_NETWORK  = 'network';   // couldn't reach our backend — token NOT consumed, retry OK
const ERR_EXPIRED  = 'expired';   // LevelUp token is expired or already used — need fresh link

function classifyError(message, status) {
  // Token expired sentinel — backend returns this specific string for 404 from LevelUp
  if (message === 'token_expired') return ERR_EXPIRED;

  // Network / gateway errors — safe to retry (token was not consumed)
  if (status === 502 || status === 503 || status === 504) return ERR_NETWORK;
  const m = (message || '').toLowerCase();
  if (m.includes('could not reach') || m.includes('failed to fetch') || m.includes('unable to connect'))
    return ERR_NETWORK;

  return ERR_DENIED;
}

const AuthenticateUser = () => {
  const { t } = useTranslation();
  const [status, setStatus]     = useState('Verifying your LevelUp account...');
  const [errorType, setErrorType] = useState(null);   // null = loading

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    let tokenParam = params.get('token');
    let relayOrigin = params.get('relay');

    // Handle malformed redirect_back URL: some SSO systems naively append "?token=TOKEN"
    // even when the redirect_back already contains "?relay=...", producing:
    //   /authenticate-user?relay=PREVIEW_URL?token=TOKEN  (two question marks)
    // In that case the browser puts everything after the first "?" in the query string,
    // so params.get('token') is null but params.get('relay') contains "PREVIEW_URL?token=TOKEN".
    if (!tokenParam && relayOrigin && relayOrigin.includes('?token=')) {
      const [cleanRelay, extractedToken] = relayOrigin.split('?token=');
      relayOrigin = cleanRelay;
      tokenParam = extractedToken;
    }

    // RELAY: if we received a LevelUp token on the production domain but the SSO
    // was initiated from a different origin (e.g. preview), forward the raw LevelUp
    // token there.  The preview /authenticate-user page will then do the real exchange.
    if (tokenParam && relayOrigin) {
      try {
        const relayUrl = new URL(relayOrigin);
        if (relayUrl.origin !== window.location.origin) {
          // Forward to the relay origin with the LevelUp token
          window.location.href = `${relayUrl.origin}/authenticate-user?token=${encodeURIComponent(tokenParam)}`;
          return;
        }
      } catch (_) { /* invalid relay URL — fall through to normal exchange */ }
    }

    // Guard against React StrictMode double-invoke AND intentional page refreshes.
    // The key is set BEFORE the async call so a simultaneous second effect returns early.
    // The LevelUp token is ONE-TIME USE — we must not call fetch-user-details twice.
    const storageKey = `sso_called_${tokenParam}`;
    if (sessionStorage.getItem(storageKey)) return;
    sessionStorage.setItem(storageKey, '1');

    if (!tokenParam) {
      setStatus('No sign-in token found. Please click "Sign in with LevelUp" to try again.');
      setErrorType(ERR_DENIED);
      return;
    }

    api.request('/auth/sso/exchange', {
      method: 'POST',
      body: JSON.stringify({ token: tokenParam })
    })
      .then(data => {
        if (data.access_token) {
          api.setToken(data.access_token);
          // Clear the SSO-pending flag so the login page doesn't show the "sign out first" banner
          sessionStorage.removeItem('levelup_sso_pending');
          // Replace the /authenticate-user URL so the back-button doesn't re-trigger
          window.history.replaceState({}, '', '/');
          window.location.href = '/';
        } else {
          throw new Error('No access token received');
        }
      })
      .catch(error => {
        const msg = error.message || '';
        const type = classifyError(msg, error.status);

        if (type === ERR_NETWORK) {
          // Token was NOT consumed — safe for user to retry
          sessionStorage.removeItem(storageKey);
          setStatus('Could not reach the server. Please check your connection and try again.');
        } else {
          // ERR_DENIED covers all other cases (401, 403, bad token, no access)
          setStatus(msg);
        }

        console.error('[SSO] Error:', type, msg);
        setErrorType(type);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goToLogin = () => {
    // Redirect to login page — user will go through LevelUp flow again for a fresh token
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen gradient-hero flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-10 text-center">
        <div className="mb-6">
          <img src="/logo-levelup-dark.svg" alt="LevelUp" className="h-8 mx-auto mb-4" />
        </div>

        {/* Loading state */}
        {!errorType && (
          <>
            <div className="animate-spin w-12 h-12 border-4 border-levelup-accent border-t-transparent rounded-full mx-auto mb-6" />
            <p className="text-levelup-dark font-semibold text-lg" data-testid="sso-status">
              {status || t('sso.verifying')}
            </p>
            <p className="text-gray-500 text-sm mt-2">{t('sso.pleaseWait')}</p>
          </>
        )}


        {/* Access denied / all non-network errors */}
        {errorType === ERR_DENIED && (
          <>
            <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-5 border-2 border-blue-200">
              <svg className="w-8 h-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <p className="text-levelup-dark font-bold text-xl mb-3" data-testid="sso-status">{t('sso.authorizationRequired')}</p>
            <p className="text-gray-600 text-sm mb-4 leading-relaxed">
              Your account does not currently have authorisation to access this portal.
              Please contact your organisation's administrator.
            </p>
            {/* Only show Reason box for unexpected technical errors, not standard access denials */}
            {status && status !== t('sso.verifying') && status !== 'token_expired' &&
             !status.toLowerCase().includes('does not currently have authorisation') && (
              <div className="w-full bg-gray-50 border border-gray-200 rounded-lg p-3 mb-5 text-left">
                <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide mb-1">Reason</p>
                <p className="text-xs text-gray-600 break-words">{status}</p>
              </div>
            )}
            <button
              onClick={() => { window.location.href = 'https://skillupwithlevelup.com'; }}
              className="w-full bg-levelup-dark text-white px-6 py-3 rounded-lg font-semibold hover:bg-levelup-darker transition-all mb-3"
              data-testid="sso-retry-btn"
            >
              {t('sso.returnToLevelUp')}
            </button>
            <button
              onClick={() => { window.location.href = 'https://skillupwithlevelup.com'; }}
              className="w-full text-gray-400 text-sm hover:text-gray-600 transition-colors"
              data-testid="sso-go-levelup-btn"
            >
              Go to LevelUp
            </button>
          </>
        )}

        {/* Token expired — user needs to return to LevelUp for a fresh link */}
        {errorType === ERR_EXPIRED && (
          <>
            <div className="w-16 h-16 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-5 border-2 border-amber-200">
              <svg className="w-8 h-8 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-levelup-dark font-bold text-xl mb-3" data-testid="sso-status">Sign-in Link Expired</p>
            <p className="text-gray-600 text-sm mb-6 leading-relaxed">
              Your LevelUp sign-in link has expired or has already been used.
              Please return to LevelUp and click the portal link again to generate a fresh one.
            </p>
            <button
              onClick={() => { window.location.href = 'https://skillupwithlevelup.com'; }}
              className="w-full bg-levelup-dark text-white px-6 py-3 rounded-lg font-semibold hover:bg-levelup-darker transition-all mb-3"
              data-testid="sso-expired-btn"
            >
              Return to LevelUp
            </button>
            <button
              onClick={() => { window.location.href = 'https://skillupwithlevelup.com'; }}
              className="w-full text-gray-400 text-sm hover:text-gray-600 transition-colors"
              data-testid="sso-expired-go-levelup-btn"
            >
              Go to LevelUp
            </button>
          </>
        )}

        {/* Network error — safe to retry */}
        {errorType === ERR_NETWORK && (
          <>
            <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-7 h-7 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
              </svg>
            </div>
            <p className="text-blue-700 font-bold text-lg mb-2" data-testid="sso-status">{t('sso.connectionError')}</p>
            <p className="text-gray-600 text-sm mb-6">{status}</p>
            <button
              onClick={() => window.location.reload()}
              className="w-full bg-levelup-dark text-white px-6 py-3 rounded-lg font-semibold hover:bg-levelup-darker transition-all mb-3"
              data-testid="sso-retry-btn"
            >
              {t('sso.tryAgainBtn')}
            </button>
            <button
              onClick={() => { window.location.href = 'https://skillupwithlevelup.com'; }}
              className="w-full text-gray-500 text-sm underline hover:text-gray-700"
              data-testid="sso-network-go-levelup-btn"
            >
              Go to LevelUp
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthenticateUser;
